





















































































































































































































































































































































































































































































































































































































































import { Component, Vue } from 'vue-property-decorator';
import { getTrialSteps, postTrialChapter, putTrialChapter, deleteTrialChapter } from '@/api/api';

interface Chapter {
  id: number;
  title: string;
  description: string;
  videoUrl: string;
  documentUrl: string;
  letterIndex: string;
  chapterLevel: string;
  position: number;
  isMandatory: boolean;
  isLocked: boolean;
  isAvailable: boolean;
  videoDuration: number | null;
}

interface TreeNode {
  chapter: Chapter;
  children: Chapter[];
}

interface GroupedStep {
  stepNumber: number;
  stepTitle: string;
  chapters: Chapter[];
}

interface ChapterForm {
  title: string;
  description: string;
  videoUrl: string;
  videoFile: File | null;
  stepTitle: string;
  isMandatory: boolean;
  documentUrl: string;
  documentFile: File | null;
  chapterLevel: string;
  videoDuration: number | null;
}


interface Toast {
  visible: boolean;
  message: string;
  type: 'success' | 'error';
}

@Component
export default class AdminTrials extends Vue {
  loading: boolean = false;
  saving: boolean = false;
  isDragOver: boolean = false;
  isDocDragOver: boolean = false;
  uploadProgress: number = 0;

  groupedSteps: GroupedStep[] = [];

  showNewStepModal: boolean = false;
  showAddChapterModal: boolean = false;
  showEditChapterModal: boolean = false;
  showDeleteConfirm: boolean = false;
  showLockConfirm: boolean = false;
  showUnavailableConfirm: boolean = false;

  targetStepNumber: number = 0;
  targetStepTitle: string = '';
  editingChapter: Chapter | null = null;
  deletingChapter: Chapter | null = null;
  lockingGroup: GroupedStep | null = null;
  unavailableGroup: GroupedStep | null = null;

  chapterForm: ChapterForm = {
      title: '', description: '', videoUrl: '', videoFile: null,
      stepTitle: '', isMandatory: false, documentUrl: '', documentFile: null, chapterLevel: '', videoDuration: null
    };

  toast: Toast = { visible: false, message: '', type: 'success' };
  toastTimer: any = null;

  async created(): Promise<void> {
    await this.loadSteps();
  }

  // ---- Tree builder ----
  buildTree(chapters: Chapter[]): TreeNode[] {
    const parents: TreeNode[] = [];
    const childMap: { [key: string]: Chapter[] } = {};

    chapters.forEach((ch: Chapter) => {
      const lvl = ch.chapterLevel || '';
      // A child level contains a dash: A-1, B-2, etc.
      if (lvl.indexOf('-') !== -1) {
        const parentLetter = lvl.split('-')[0];
        if (!childMap[parentLetter]) childMap[parentLetter] = [];
        childMap[parentLetter].push(ch);
      }
    });

    chapters.forEach((ch: Chapter) => {
      const lvl = ch.chapterLevel || '';
      if (lvl.indexOf('-') === -1) {
        parents.push({ chapter: ch, children: childMap[lvl] || [] });
      }
    });

    // Chapters with no level fall back to letterIndex as parent
    if (parents.length === 0) {
      chapters.forEach((ch: Chapter) => {
        parents.push({ chapter: ch, children: [] });
      });
    }

    return parents;
  }

  indexToLetter(index: number): string {
    let letter = '';
    let i = index;
    while (i >= 0) {
      letter = String.fromCharCode(i % 26 + 65) + letter;
      i = Math.floor(i / 26) - 1;
    }
    return letter;
  }

  nextTopLevelForStep(stepNumber: number): string {
    const group = this.groupedSteps.find(g => g.stepNumber === stepNumber);
    if (!group) return 'A';
    const tops = group.chapters
      .map((ch: any) => ch.chapterLevel || '')
      .filter((l: string) => l.length > 0 && l.indexOf('-') === -1);
    const unique = tops.filter((v: string, i: number, a: string[]) => a.indexOf(v) === i);
    return this.indexToLetter(unique.length);
  }

  nextSubLevelForParent(stepNumber: number, parentLevel: string): string {
    const group = this.groupedSteps.find(g => g.stepNumber === stepNumber);
    if (!group) return parentLevel + '-1';
    const subs = group.chapters
      .map((ch: any) => ch.chapterLevel || '')
      .filter((l: string) => l.indexOf('-') !== -1 && l.split('-')[0] === parentLevel);
    return parentLevel + '-' + (subs.length + 1);
  }

  // ---- Lock helpers ----
  isStepFullyLocked(group: GroupedStep): boolean {
    if (!group.chapters || group.chapters.length === 0) return false;
    return group.chapters.every(function(ch: Chapter) { return ch.isLocked === true; });
  }


  isStepFullyUnavailable(group: GroupedStep): boolean {
    if (!group.chapters || group.chapters.length === 0) return false;
    return group.chapters.every(function(ch: Chapter) { return !ch.isAvailable; });
  }


  openUnavailableConfirm(group: GroupedStep): void {
    this.unavailableGroup = group;
    this.showUnavailableConfirm = true;
  }

  // ---- Toast ----
  showToast(message: string, type: 'success' | 'error' = 'success'): void {
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toast = { visible: true, message, type };
    this.toastTimer = setTimeout(() => { this.toast.visible = false; }, 4000);
  }

  extractErrorMessage(e: any, fallback: string): string {
    const err = e as any;
    if (err.response && err.response.status === 413) return 'Le fichier est trop lourd.';
    if (err.response && err.response.data) {
      if (err.response.data.message) return err.response.data.message;
      if (err.response.data.error) return err.response.data.error;
      if (typeof err.response.data === 'string') return err.response.data;
    }
    if (err.message) return err.message;
    return fallback;
  }

  // ---- Load ----
  async loadSteps(): Promise<void> {
    this.loading = true;
    try {
      const res = await getTrialSteps();
      this.groupedSteps = res.data;
    } catch (e) {
      this.showToast(this.extractErrorMessage(e, 'Erreur lors du chargement des etapes.'), 'error');
    } finally {
      this.loading = false;
    }
  }

  // ---- File helpers ----
  toBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  }

handleFileUpload(event: any): void {
  const files = event.target.files;
  if (files && files.length > 0) {
    const file = files[0];
    this.chapterForm.videoFile = file;
    this.uploadProgress = 0;
    const video = document.createElement('video');
    video.preload = 'metadata';
    const url = URL.createObjectURL(file);
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      if (isFinite(video.duration) && video.duration > 0) {
        this.chapterForm.videoDuration = Math.round(video.duration);
      }
    };
    video.src = url;
  }
}

onDrop(event: DragEvent): void {
  this.isDragOver = false;
  if (event.dataTransfer && event.dataTransfer.files.length > 0) {
    const file = event.dataTransfer.files[0];
    if (file.type.startsWith('video/')) {
      this.chapterForm.videoFile = file;
      this.uploadProgress = 0;
      const video = document.createElement('video');
      video.preload = 'metadata';
      const url = URL.createObjectURL(file);
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        if (isFinite(video.duration) && video.duration > 0) {
          this.chapterForm.videoDuration = Math.round(video.duration);
        }
      };
      video.src = url;
    }
  }
}

  triggerFileInput(): void {
    if (this.uploadProgress > 0 && this.uploadProgress < 100) return;
    const input = this.$refs.videoInput as HTMLInputElement;
    if (input) input.click();
  }

  removeFile(): void {
    this.chapterForm.videoFile = null;
    this.uploadProgress = 0;
    const input = this.$refs.videoInput as HTMLInputElement;
    if (input) input.value = '';
  }

  handleDocUpload(event: any): void {
    const files = event.target.files;
    if (files && files.length > 0) { this.chapterForm.documentFile = files[0]; }
  }

  onDocDrop(event: DragEvent): void {
    this.isDocDragOver = false;
    if (event.dataTransfer && event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0];
      const allowed = ['application/pdf', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
      if (allowed.indexOf(file.type) !== -1 || file.name.endsWith('.pdf') || file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        this.chapterForm.documentFile = file;
      }
    }
  }

  triggerDocInput(): void {
    const input = this.$refs.docInput as HTMLInputElement;
    if (input) input.click();
  }

  removeDoc(): void {
    this.chapterForm.documentFile = null;
    const input = this.$refs.docInput as HTMLInputElement;
    if (input) input.value = '';
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  simulateUploadProgress(onDone: () => Promise<void>): Promise<void> {
    return new Promise((resolve, reject) => {
      this.uploadProgress = 0;
      const hasFile = this.chapterForm.videoFile !== null;
      if (!hasFile) { onDone().then(() => resolve()).catch((e) => reject(e)); return; }
      const interval = setInterval(() => {
        if (this.uploadProgress < 85) { this.uploadProgress += Math.random() * 12 + 3; if (this.uploadProgress > 85) this.uploadProgress = 85; }
      }, 150);
      onDone().then(() => { clearInterval(interval); this.uploadProgress = 100; resolve(); }).catch((e) => { clearInterval(interval); this.uploadProgress = 0; reject(e); });
    });
  }

resetForm(): void {
  this.chapterForm = { title: '', description: '', videoUrl: '', videoFile: null, stepTitle: '', isMandatory: false, documentUrl: '', documentFile: null, chapterLevel: '', videoDuration: null };
  this.uploadProgress = 0;
  this.isDragOver = false;
  this.isDocDragOver = false;
  const vi = this.$refs.videoInput as HTMLInputElement;
  if (vi) vi.value = '';
  const di = this.$refs.docInput as HTMLInputElement;
  if (di) di.value = '';
}

  // ---- Modal openers ----
  openNewStepChapterModal(): void {
    this.resetForm();
    this.showNewStepModal = true;
  }

  openNewChapterModal(stepNumber: number, stepTitle: string): void {
    this.targetStepNumber = stepNumber;
    this.targetStepTitle = stepTitle;
    this.resetForm();
    this.chapterForm.chapterLevel = this.nextTopLevelForStep(stepNumber);
    this.showAddChapterModal = true;
  }

  openAddChildModal(parentChapter: Chapter, group: any): void {
    this.targetStepNumber = group.stepNumber;
    this.targetStepTitle = group.stepTitle;
    this.resetForm();
    const parentLevel = parentChapter.chapterLevel || parentChapter.letterIndex;
    this.chapterForm.chapterLevel = this.nextSubLevelForParent(group.stepNumber, parentLevel);
    this.showAddChapterModal = true;
  }

openEditChapterModal(chapter: Chapter): void {
  this.editingChapter = chapter;
  this.uploadProgress = 0;
  this.isDragOver = false;
  this.isDocDragOver = false;
  this.chapterForm = {
    title: chapter.title,
    description: chapter.description,
    videoUrl: chapter.videoUrl || '',
    videoFile: null,
    stepTitle: '',
    isMandatory: chapter.isMandatory || false,
    documentUrl: chapter.documentUrl || '',
    documentFile: null,
    chapterLevel: chapter.chapterLevel || '',
    videoDuration: chapter.videoDuration || null
  };
  this.showEditChapterModal = true;
}

  confirmDeleteChapter(chapter: Chapter): void {
    this.deletingChapter = chapter;
    this.showDeleteConfirm = true;
  }

  openLockConfirm(group: GroupedStep): void {
    this.lockingGroup = group;
    this.showLockConfirm = true;
  }



  async confirmUnavailableStep(): Promise<void> {
      if (!this.unavailableGroup) return;
      this.saving = true;
      try {
        const group = this.unavailableGroup;
        
        // FIX: Remove the "!" here. 
        // If it's fully unavailable (true), we want to make it available (true).
        // If it's available (false), we want to make it unavailable (false).
        const newAvailable = this.isStepFullyUnavailable(group); 

        const chapters = group.chapters;
        for (let i = 0; i < chapters.length; i++) {
          await putTrialChapter(chapters[i].id, { isAvailable: newAvailable });
        }
        await this.loadSteps();
        this.showUnavailableConfirm = false;
        this.unavailableGroup = null;
        this.showToast(newAvailable
          ? 'Étape maintenant disponible pour les utilisateurs.'
          : 'Étape masquée : les utilisateurs verront une page "Bientôt disponible".');
      } catch (e) {
        this.showToast(this.extractErrorMessage(e, 'Erreur lors de la mise à jour de la disponibilité.'), 'error');
      } finally {
        this.saving = false;
      }
    }



  // ---- Save actions ----
async saveNewStepChapter(): Promise<void> {
  if (!this.chapterForm.title.trim()) return;
  this.saving = true;
  try {
    const nextStep = this.groupedSteps.length > 0
      ? Math.max(...this.groupedSteps.map(g => g.stepNumber)) + 1
      : 1;

    const payload: any = {
      stepNumber: nextStep,
      stepTitle: this.chapterForm.stepTitle,
      title: this.chapterForm.title,
      description: this.chapterForm.description,
      isMandatory: this.chapterForm.isMandatory ? '1' : '0',
      chapterLevel: this.indexToLetter(0)
    };

    if (this.chapterForm.videoDuration !== null && this.chapterForm.videoDuration !== undefined) {
      payload.videoDuration = String(this.chapterForm.videoDuration);
    }
    if (this.chapterForm.videoFile) {
      payload.videoFile = this.chapterForm.videoFile;
    }
    if (this.chapterForm.documentFile) {
      payload.documentFile = this.chapterForm.documentFile;
    }

    const fd = this.buildFormData(payload);
    await this.simulateUploadProgress(async () => { await postTrialChapter(fd); });
    await this.loadSteps();
    setTimeout(() => { this.showNewStepModal = false; this.showToast('Etape creee avec succes !'); }, 400);
  } catch (e) {
    this.showToast(this.extractErrorMessage(e, 'Erreur lors de la creation.'), 'error');
  } finally {
    this.saving = false;
  }
}


async saveAddChapter(): Promise<void> {
  if (!this.chapterForm.title.trim()) return;
  this.saving = true;
  try {
    const payload: any = {
      stepNumber: this.targetStepNumber,
      stepTitle: this.targetStepTitle,
      title: this.chapterForm.title,
      description: this.chapterForm.description,
      isMandatory: this.chapterForm.isMandatory ? '1' : '0',
      chapterLevel: this.chapterForm.chapterLevel
    };

    if (this.chapterForm.videoDuration !== null && this.chapterForm.videoDuration !== undefined) {
      payload.videoDuration = String(this.chapterForm.videoDuration);
    }
    if (this.chapterForm.videoFile) {
      payload.videoFile = this.chapterForm.videoFile;
    }
    if (this.chapterForm.documentFile) {
      payload.documentFile = this.chapterForm.documentFile;
    }

    const fd = this.buildFormData(payload);
    await this.simulateUploadProgress(async () => { await postTrialChapter(fd); });
    await this.loadSteps();
    setTimeout(() => { this.showAddChapterModal = false; this.showToast('Chapitre ajoute avec succes !'); }, 400);
  } catch (e) {
    this.showToast(this.extractErrorMessage(e, "Erreur lors de l'ajout."), 'error');
  } finally {
    this.saving = false;
  }
}



async saveEditChapter(): Promise<void> {
  if (!this.editingChapter || !this.chapterForm.title.trim()) return;
  this.saving = true;
  try {
    const payload: any = {
      title: this.chapterForm.title,
      description: this.chapterForm.description,
      isMandatory: this.chapterForm.isMandatory ? '1' : '0',
      chapterLevel: this.chapterForm.chapterLevel
    };

    if (this.chapterForm.videoDuration !== null && this.chapterForm.videoDuration !== undefined) {
      payload.videoDuration = String(this.chapterForm.videoDuration);
    }
    if (this.chapterForm.videoFile) {
      payload.videoFile = this.chapterForm.videoFile;
    }
    if (this.chapterForm.documentFile) {
      payload.documentFile = this.chapterForm.documentFile;
    }

    const fd = this.buildFormData(payload);
    await this.simulateUploadProgress(async () => { await putTrialChapter(this.editingChapter!.id, fd); });
    await this.loadSteps();
    setTimeout(() => { this.showEditChapterModal = false; this.editingChapter = null; this.showToast('Chapitre mis a jour !'); }, 400);
  } catch (e) {
    this.showToast(this.extractErrorMessage(e, 'Erreur lors de la mise a jour.'), 'error');
  } finally {
    this.saving = false;
  }
}


buildFormData(payload: any): FormData {
  const fd = new FormData();
  Object.keys(payload).forEach(key => {
    if (payload[key] !== undefined && payload[key] !== null) {
      fd.append(key, payload[key]);
    }
  });
  return fd;
}


  async executeDelete(): Promise<void> {
    if (!this.deletingChapter) return;
    this.saving = true;
    try {
      await deleteTrialChapter(this.deletingChapter.id);
      await this.loadSteps();
      this.showDeleteConfirm = false;
      this.deletingChapter = null;
      this.showToast('Chapitre supprime avec succes !');
    } catch (e) {
      this.showToast(this.extractErrorMessage(e, 'Erreur lors de la suppression.'), 'error');
    } finally {
      this.saving = false;
    }
  }


  async confirmLockStep(): Promise<void> {
  if (!this.lockingGroup) return;
  this.saving = true;
  try {
    const group = this.lockingGroup;
    const newLocked = !this.isStepFullyLocked(group);
    const chapters = group.chapters;
    for (let i = 0; i < chapters.length; i++) {
      await putTrialChapter(chapters[i].id, { isLocked: newLocked });
    }
    await this.loadSteps();
    this.showLockConfirm = false;
    this.lockingGroup = null;
    this.showToast(newLocked
      ? 'Etape verrouillee.'
      : 'Etape deverrouillee.');
  } catch (e) {
    this.showToast(this.extractErrorMessage(e, 'Erreur lors du verrouillage.'), 'error');
  } finally {
    this.saving = false;
  }
}


  beforeDestroy(): void {
    if (this.toastTimer) clearTimeout(this.toastTimer);
  }
}
